import {
  Box,
  Grid,
  GridItem,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EventDetailDemographicsSectionData,
  KYDEventDetailDemographicsItem,
} from "@common/interfaces/KYDEvent";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { InlineText } from "./ConversionSection";
import EventDetailTitle from "../../../EventDetailTitle";

const CustomTooltip = ({
  labelPrefix,
  valuePrefix,
  active,
  label,
  payload,
}: TooltipProps<number, string> & {
  labelPrefix?: string;
  valuePrefix?: string;
}) => {
  if (active) {
    return (
      <Stack
        p={3}
        spacing={1}
        borderColor={"gray.300"}
        borderWidth={"1px"}
        rounded={"md"}
        bg="white"
      >
        <Text fontSize={"xs"} fontWeight={"bold"}>
          {labelPrefix}
          {payload[0]?.payload?.label}
        </Text>
        <Text fontSize={"xs"}>
          {valuePrefix}
          {payload[0]?.payload?.percentage}% ({payload[0]?.payload?.value})
        </Text>
      </Stack>
    );
  }

  return null;
};

const genderColors = ["#1da8ab", "#aa3388", "#aaaaaa"];

const DemographicsSection = ({
  data,
}: {
  data: EventDetailDemographicsSectionData;
}) => {
  const hideAge = data.demographics?.age?.length === 0;

  return (
    <VStack>
      <Stack direction={"column"} w="100%">
        <EventDetailTitle>Demographics</EventDetailTitle>
      </Stack>
      <Grid
        pt={8}
        w="full"
        flexWrap={"wrap"}
        flexBasis={"50%"}
        my={4}
        gap={2}
        rowGap={8}
        columnGap={8}
        // direction={["column", "row"]}
        templateColumns={["repeat(1, 1fr)", null, "repeat(2, 1fr)"]}
      >
        {data.demographics?.gender ? (
          <GridItem w="100%%">
            <VStack height="400">
              <Text fontWeight={"bold"}>Gender</Text>
              <Stack
                direction={["column-reverse", "row"]}
                spacing={2}
                height={"400"}
                width={"full"}
                alignItems={["initial", "center"]}
                justifyContent={"space-between"}
              >
                <VStack width={["100%"]} spacing={2}>
                  {data.demographics?.gender
                    ?.reduce<
                      {
                        label: string;
                        value: number;
                        percentage: number;
                        color: string;
                      }[]
                    >((byGender, item, index) => {
                      const sameAge = byGender.find(
                        (gender) => gender.label === item.label
                      ) || {
                        label: item.label,
                        value: 0,
                        percentage: 0,
                        color: genderColors[index],
                      };

                      return byGender.concat({
                        label: item.label,
                        percentage: sameAge.percentage + item.percentage,
                        value: sameAge.value + item.value,
                        color: sameAge.color || genderColors[index],
                      });
                    }, [])
                    .map((item) => (
                      <HStack
                        w="full"
                        spacing={2}
                        borderBottomWidth={1}
                        borderColor="gray.200"
                        p={2}
                        justifyContent={"space-between"}
                      >
                        <HStack spacing={2}>
                          <Box
                            rounded={"full"}
                            w={5}
                            h={5}
                            background={item.color}
                          ></Box>
                          <Text fontSize={"medium"}>{item.label}</Text>
                        </HStack>
                        <Text fontWeight={"bold"}>{item.percentage}%</Text>
                      </HStack>
                    ))}
                </VStack>
                <ResponsiveContainer>
                  <PieChart width={180} height={200}>
                    <Pie
                      data={data.demographics.gender}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                    >
                      {data.demographics?.gender?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={genderColors[index]}
                          values={`${entry.value} (${entry.percentage}%)`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      cursor={{ fill: "#eee" }}
                      labelStyle={{ color: "black" }}
                      //@ts-ignore
                      content={
                        <CustomTooltip labelPrefix="Gender: " valuePrefix="" />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Stack>
            </VStack>
          </GridItem>
        ) : null}
        {data.demographics?.age?.length ? (
          <GridItem width={"100%"}>
            <VStack height={"400"}>
              <Text fontWeight={"bold"}>Age</Text>
              <ResponsiveContainer>
                <BarChart
                  width={200}
                  height={200}
                  data={data.demographics?.age}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis interval={1} name="value" />
                  <Bar dataKey={"value"} fill="#8884d8" />
                  <Tooltip
                    cursor={{ fill: "#eee" }}
                    labelStyle={{ color: "black" }}
                    //@ts-ignore
                    content={
                      <CustomTooltip
                        labelPrefix="Age: "
                        valuePrefix="Total: "
                      />
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
            </VStack>
          </GridItem>
        ) : null}
        {data.demographics?.city ? (
          <GridItem width={"100%"}>
            <VStack>
              <Text fontWeight={"bold"}>By location</Text>
              <VStack width={["100%"]} spacing={2}>
                {data.demographics?.city?.map((item) => (
                  <HStack
                    w="full"
                    spacing={2}
                    borderBottomWidth={1}
                    borderColor="gray.200"
                    p={2}
                    justifyContent={"space-between"}
                  >
                    <HStack spacing={2}>
                      <Text fontSize={"medium"}>{item.label}</Text>
                    </HStack>
                    <Text fontWeight={"bold"}>{item.percentage}%</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </GridItem>
        ) : null}
        {data.demographics?.country ? (
          <GridItem width={"100%"}>
            <VStack>
              <Text fontWeight={"bold"}>By country</Text>
              <VStack width={["100%"]} spacing={2}>
                {data.demographics?.country.map((item) => (
                  <HStack
                    w="full"
                    spacing={2}
                    borderBottomWidth={1}
                    borderColor="gray.200"
                    p={2}
                    justifyContent={"space-between"}
                  >
                    <HStack spacing={2}>
                      <Text fontSize={"medium"}>{item.label}</Text>
                    </HStack>
                    <Text fontWeight={"bold"}>{item.percentage}%</Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </GridItem>
        ) : null}
      </Grid>
    </VStack>
  );
};

export default DemographicsSection;
